import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  HostListener
} from '@angular/core';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ChatMessage, ChatRenameEvent, DEOAgentArtifact, Feedback, IResponse, MoveToProjectModalResult, RenameChatModalResult } from '../../models/chat.models';
import { ConversationStreamEvent, DeoConversation } from '../../models/deo-conversation.model';
import { AgentDefinition } from 'app/modules/agent-library/models/agent-definition.model';
import { DeoAiAssistantRenameChatComponent } from '../deo-ai-assistant-rename-chat/deo-ai-assistant-rename-chat.component';
import { DeoAiAssistantMoveToProjectComponent } from '../deo-ai-assistant-move-to-project/deo-ai-assistant-move-to-project.component';
import { MessageFormatterService } from '../../services/message-formatter.service';
import { FileSelectionService } from 'app/shared/services/file-selection/file-selection.service';
import { VoiceToTextService } from 'app/shared/services/voice-to-text.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AIAgentService } from '../../services/ai-agent.service';
import { getApiErrorMessage, getResponseErrorMessage } from '../../services/api-error.helper';
import { formatStreamActivityLabel } from '../../services/stream-activity.helper';
import { AuthService } from 'app/services/auth/auth.service';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { getArtifactIcon, getArtifactTypeColor } from '../../models/artifact-types.constants';
import { DislikeFeedback } from '../../deo-ai-assist-feedback/deo-ai-assist-feedback.component';
import { DeoConversationService } from '../../services/deo-conversation.service';
import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';
import { DEOAiAssistantService } from '../../services/deo-ai-assistant.service';
import { GainsApplyCancelService } from 'app/shared/services/gains-apply-delete-service/gains-apply-delete.service';
import { FeatureFlagsService } from 'app/shared/services/feature-flags/feature-flags.service';
import { formatChatMessageTimestamp } from 'app/shared/util/chat-timestamp.util';
import {
  FEATURE_FLAG_DEO_AGENT_FEEDBACK,
  FEATURE_FLAG_DEO_AGENT_LIVE_TRANSCRIPTION,
  FEATURE_FLAG_DEO_AGENT_RENAME_CHAT,
  FEATURE_FLAG_DEO_AGENT_PROJECTS,
  FEATURE_FLAG_DEO_AGENT_ATTACH_FILE,
  FEATURE_FLAG_DEO_AGENT_STOP_BUTTON,
  FEATURE_FLAG_DEO_AGENT_CONVERSATION_CHIPS
} from 'app/shared/services/feature-flags/feature-flags.constants';
import { pendingClarificationOptions } from '../../services/clarification.helper';
import { isTempSessionId } from '../../models/temp-session.constants';

const FEEDBACK_FAILED_MESSAGE = 'Failed to send feedback. Please try again.';
const FEEDBACK_REMOVE_FAILED_MESSAGE = 'Failed to remove feedback. Please try again.';

/** send-message answers 403 when a viewer replies in a chat shared with them read-only. */
const HTTP_FORBIDDEN = 403;

@Component({
  standalone: false,
  selector: 'deo-ai-assistant-existing-chat',
  templateUrl: './deo-ai-assistant-existing-chat.component.html',
  styleUrls: ['./deo-ai-assistant-existing-chat.component.scss']
})
export class DeoAiAssistantExistingChatComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('messagesContainer') private readonly messagesContainer!: ElementRef;

  @Input() conversation: DeoConversation | null = null;
  @Input() currentSessionId: string | null = null;
  @Input() isArtifactDrawerOpen = false;

  @Input() chatTitle = 'New Chat';
  @Input() isFavorite = false;

  /**
   * The agent answering this conversation, or null when it is DEO's.
   *
   * Resolved by the host, which owns the selector and knows whether that agent has anything to say;
   * this view stays presentational and reads no agent service, as `<gains-agent-card>` does.
   */
  @Input() answeringAgent: AgentDefinition | null = null;

  /** Someone else's chat in a project shared with "Everyone": it can be read, never replied to or changed. */
  @Input() isReadOnly = false;
  /** Who wrote the user turns of a read-only chat. */
  @Input() ownerLabel = '';

  @Output() favoriteChanged = new EventEmitter<boolean>();
  @Output() chatRenamed$ = new EventEmitter<ChatRenameEvent>();
  @Output() chatDeleted$ = new EventEmitter<string>();
  /** This view is the single owner of the open chat's artifacts request; the host attaches the inline cards from it. */
  @Output() artifactsLoaded$ = new EventEmitter<DEOAgentArtifact[]>();

  currentMessage = '';
  selectedFile: File | null = null;
  isListening = false;
  voiceError: string | null = null;
  isEllipsisActive = false;
  isArtifactsPanelOpen = false;
  isFeedbackEnabled = false;
  isLiveTranscriptionEnabled = false;
  isAttachFileEnabled = false;
  isStopButtonEnabled = false;
  isConversationChipsEnabled = false;
  readonly MAX_FILE_SIZE = 5 * 1024 * 1024;
  chatArtifacts: DEOAgentArtifact[] = [];

  private _artifactsSubscription?: Subscription;
  private _requestedArtifactsSessionId: string | null = null;

  /** Thumb state from before an in-flight rating, so a rejected one can be rolled back. */
  private readonly _feedbackBeforeSubmit = new WeakMap<ChatMessage, boolean | null | undefined>();
  private readonly _feedbackInFlight = new WeakSet<ChatMessage>();

  /** Whether the reader opened or shut a message's progress panel; otherwise it follows the answer. */
  private readonly _progressCollapsedByUser = new WeakMap<ChatMessage, boolean>();

  // Scroll management
  private _shouldAutoScroll = true;
  private _savedScrollPosition = 0;

  private _baseText = '';
  private _composerClientId: string | null = null;
  private readonly _rejectedAsReadOnly = new Set<string>();
  private readonly _destroy$ = new Subject<void>();

  currentChatMenuItems: EllipsisMenuItem[] = [
    { title: 'Star', icon: 'favorite.svg' },
    { title: 'Rename', icon: 'rename.svg' },
    { title: 'Move to Project', icon: 'MoveProject.svg' },
    { title: 'Delete', warning: true, icon: 'trash-2.svg', divider: true }
  ];

  constructor(
    private readonly _messageFormatter: MessageFormatterService,
    private readonly _fileSelectionService: FileSelectionService,
    private readonly _voiceService: VoiceToTextService,
    private readonly _aiAgentService: AIAgentService,
    private readonly _authService: AuthService,
    private readonly _gainsToastService: GainsToastService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _deoAiAssistantService: DEOAiAssistantService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _modalService: NzModalService,
    private readonly _gainsApplyCancelService: GainsApplyCancelService,
    private readonly _conversationService: DeoConversationService
  ) {}

  /** Messages render straight from the live conversation the streaming engine mutates in place. */
  get messages(): ChatMessage[] {
    return this.conversation?.messages ?? [];
  }

  /** True while this conversation is streaming from the backend, which is the only kind of answer that can be stopped. */
  get isStreaming(): boolean {
    return this._conversationService.isStreaming(this.conversation);
  }

  /** Retained for the template's disabled states; covers a streamed answer and an agent's alike. */
  get isLoading(): boolean {
    return this._conversationService.isBusy(this.conversation);
  }

  /** The host's own flag, or a 403 from the backend when the host could not tell the chat was shared. */
  get readOnly(): boolean {
    return this.isReadOnly || (!!this.conversation && this._rejectedAsReadOnly.has(this.conversation.clientId));
  }

  /** Quick replies to a clarifying question, offered under the last message while it is unanswered. */
  get clarificationOptions(): string[] {
    return this.isConversationChipsEnabled && !this.readOnly ? pendingClarificationOptions(this.messages) : [];
  }

  isLastMessage(message: ChatMessage): boolean {
    return this.messages.at(-1) === message;
  }

  /** A quick reply is sent as it is, through the same path as a typed message. */
  onClarificationChosen(reply: string): void {
    if (this.isResponseInProgress) {
      return;
    }

    this.currentMessage = reply;
    this.onSendMessage();
  }

  /** Who the bubble is attributed to: the reader (or the owner of a shared chat), the answering agent, or DEO. */
  messageAuthor(message: ChatMessage): string {
    if (message.role === 'user') {
      return this.readOnly && this.ownerLabel ? this.ownerLabel : 'You';
    }

    return message.agentReply?.agentName ?? 'DEO AI Assist';
  }

  ngOnInit(): void {
    this.isFeedbackEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_FEEDBACK);
    this.isLiveTranscriptionEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_LIVE_TRANSCRIPTION);
    this.isAttachFileEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_ATTACH_FILE);
    this.isStopButtonEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_STOP_BUTTON);
    this.isConversationChipsEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_CONVERSATION_CHIPS);

    if (!this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_RENAME_CHAT)) {
      this.currentChatMenuItems = this.currentChatMenuItems.filter(item => item.title !== 'Rename');
    }

    if (!this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_PROJECTS)) {
      this.currentChatMenuItems = this.currentChatMenuItems.filter(item => item.title !== 'Move to Project' && item.title !== 'Remove from Project');
    }

    if (this.currentChatMenuItems[0]?.divider) {
      this.currentChatMenuItems = [{ ...this.currentChatMenuItems[0], divider: false }, ...this.currentChatMenuItems.slice(1)];
    }

    this.updateFavoriteMenuItem();

    this._conversationService.streamEvents$.pipe(takeUntil(this._destroy$)).subscribe(event => this._onConversationEvent(event));

    this._voiceService.isListening$.pipe(takeUntil(this._destroy$)).subscribe(isListening => {
      this.isListening = isListening;
      this._cdr.markForCheck();
    });

    this._voiceService.transcription$.pipe(takeUntil(this._destroy$)).subscribe(transcription => {
      if (transcription.isFinal) {
        this._baseText = this._baseText ? this._baseText + ' ' + transcription.transcript : transcription.transcript;
        this.onMessageChange(this._baseText);
      } else {
        this.onMessageChange(this._baseText ? this._baseText + ' ' + transcription.transcript : transcription.transcript);
      }
      this._cdr.markForCheck();
    });

    this._voiceService.error$.pipe(takeUntil(this._destroy$)).subscribe(error => {
      this.voiceError = error;
      this._cdr.markForCheck();

      if (error) {
        setTimeout(() => {
          this.voiceError = null;
          this._cdr.markForCheck();
        }, 5000);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isFavorite']) {
      this.updateFavoriteMenuItem();
    }

    // Switching to a different conversation re-binds the view to that conversation's live messages.
    if (changes['conversation']) {
      this._shouldAutoScroll = true;
    }

    if (this._hasSwitchedConversation()) {
      this._restoreComposer();
    }

    if (this._hasSwitchedChat(changes)) {
      // The artifacts of the chat we just left must never survive the switch: drop them now so the
      // counter shows only this chat's artifacts while the backend list is being fetched.
      this._resetChatArtifacts();
      this._loadChatArtifacts();
    } else if (changes['conversation']) {
      // Same chat, new object reference (streaming rebinds it): keep the artifacts already loaded.
      this.chatArtifacts = this._sortArtifacts(this._mergeArtifacts(this.chatArtifacts, this._collectMessageArtifacts()));
    }

    if (changes['isArtifactDrawerOpen']) {
      const wasOpen = changes['isArtifactDrawerOpen'].previousValue;
      const isNowOpen = changes['isArtifactDrawerOpen'].currentValue;

      if (!wasOpen && isNowOpen && this.messagesContainer) {
        this._savedScrollPosition = this.messagesContainer.nativeElement.scrollTop;
        this._shouldAutoScroll = false;
      } else if (wasOpen && !isNowOpen) {
        setTimeout(() => {
          if (this.messagesContainer && this._savedScrollPosition > 0) {
            this.messagesContainer.nativeElement.scrollTop = this._savedScrollPosition;
            this._savedScrollPosition = 0;
            this._checkIfAtBottom();
          }
        }, 50);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.messagesContainer) {
      this.messagesContainer.nativeElement.addEventListener('scroll', () => this._checkIfAtBottom());
    }
  }

  ngAfterViewChecked(): void {
    if (this._shouldAutoScroll && !this.isArtifactDrawerOpen) {
      this._scrollToBottom();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._artifactsSubscription?.unsubscribe();
  }

  /**
   * Reacts to engine events for the conversation currently in view. The DeoConversationService keeps the
   * stream alive across navigation, so this only handles view concerns: scrolling, change detection, and
   * refreshing this chat's artifacts when its response completes.
   */
  private _onConversationEvent(event: ConversationStreamEvent): void {
    if (!this.conversation || event.clientId !== this.conversation.clientId) {
      return;
    }

    if (event.type === 'updated') {
      this._shouldAutoScroll = true;
      this._cdr.markForCheck();
    } else if (event.type === 'completed') {
      this._loadChatArtifacts(event.sessionId);
      this._cdr.markForCheck();
    } else if (event.type === 'error' && event.status === HTTP_FORBIDDEN) {
      this._rejectedAsReadOnly.add(event.clientId);
      this._cdr.markForCheck();
    }
  }

  // ── Input bar state ──────────────────────────────────────────────
  // The footer button is either "send" (idle) or "stop" (a response is streaming).
  // These getters keep that decision in one readable place instead of in the template.

  /** True while the assistant is producing a response (streaming tokens, or an agent answering). */
  get isResponseInProgress(): boolean {
    return this._conversationService.isBusy(this.conversation);
  }

  /** Show the stop button only when the feature is enabled and a *streamed* response is running: an agent turn has no stream to stop. */
  get canStopResponse(): boolean {
    return this.isStopButtonEnabled && this.isStreaming;
  }

  onMessageChange(text: string): void {
    this.currentMessage = text;
    this._conversationService.setDraft(this.conversation?.clientId, text);
  }

  /** The send button is disabled when there is nothing to send or a response is already in progress. */
  get isSendDisabled(): boolean {
    return !this.currentMessage.trim() || this.isResponseInProgress;
  }

  onSendMessage(): void {
    const messageContent = this.currentMessage.trim();
    if (!messageContent || this.isResponseInProgress || !this.conversation || this.readOnly) {
      return;
    }

    const attachment = this.selectedFile ? { name: this.selectedFile.name, size: this.selectedFile.size, type: this.selectedFile.type } : undefined;

    this.onMessageChange('');
    this.selectedFile = null;
    this._baseText = '';
    this._shouldAutoScroll = true;

    // A question put to a named agent is answered locally; the backend has no agent to route it to.
    if (this.answeringAgent) {
      this._conversationService.sendToAgent(this.conversation, messageContent, this.answeringAgent);
      return;
    }

    this._conversationService.send(this.conversation, messageContent, attachment);
  }

  /**
   * Cancels the response the user no longer wants to wait for. The service aborts the underlying request
   * and settles the half-finished message; the conversation returns to idle.
   */
  onStopStreaming(): void {
    if (!this.isResponseInProgress || !this.conversation) {
      return;
    }
    this._conversationService.stop(this.conversation);
    this.currentMessage = this._conversationService.getDraft(this.conversation.clientId);
    this._baseText = '';
    this._cdr.markForCheck();
  }

  getDisplayedContent(message: ChatMessage): string {
    return this._conversationService.getDisplayedContent(message);
  }

  /** Says what the backend is doing after the answer text, so a long artifact build never reads as a hang. */
  getActivityLabel(message: ChatMessage): string {
    return formatStreamActivityLabel(message.activity);
  }

  /** Open while the answer is still being worked out, collapsed once it has started, unless the reader chose. */
  isProgressCollapsed(message: ChatMessage): boolean {
    return this._progressCollapsedByUser.get(message) ?? !!message.content;
  }

  toggleProgress(message: ChatMessage): void {
    this._progressCollapsedByUser.set(message, !this.isProgressCollapsed(message));
  }

  /** True when the inputs moved the view to a different chat, as opposed to updating the one already shown. */
  private _hasSwitchedChat(changes: SimpleChanges): boolean {
    if (changes['currentSessionId'] && changes['currentSessionId'].previousValue !== changes['currentSessionId'].currentValue) {
      return true;
    }

    const conversationChange = changes['conversation'];
    if (!conversationChange) {
      return false;
    }

    // `clientId` is stable across the temp -> real session id swap, so it identifies the chat itself.
    const previousConversation = conversationChange.previousValue as DeoConversation | null;
    const currentConversation = conversationChange.currentValue as DeoConversation | null;
    return previousConversation?.clientId !== currentConversation?.clientId;
  }

  /** Compares `clientId`, not the session id, so the temp -> real id swap of a new chat keeps its composer. */
  private _hasSwitchedConversation(): boolean {
    return (this.conversation?.clientId ?? null) !== this._composerClientId;
  }

  /** Each chat keeps its own unsent text; the attachment and dictation never follow the user to another chat. */
  private _restoreComposer(): void {
    this._composerClientId = this.conversation?.clientId ?? null;
    this.currentMessage = this._conversationService.getDraft(this._composerClientId);
    this.selectedFile = null;
    this._baseText = '';
    if (this.isListening) {
      this._voiceService.stopListening();
    }
  }

  /** Rebuilds the artifact list from the chat now in view only, discarding whatever the previous chat left behind. */
  private _resetChatArtifacts(): void {
    this._artifactsSubscription?.unsubscribe();
    this._artifactsSubscription = undefined;
    this._requestedArtifactsSessionId = null;
    this.chatArtifacts = this._sortArtifacts(this._collectMessageArtifacts());
    this._cdr.markForCheck();
  }

  private _loadChatArtifacts(sessionIdOverride?: string): void {
    const sessionId = sessionIdOverride ?? this.currentSessionId;

    if (!sessionId || isTempSessionId(sessionId)) {
      this.chatArtifacts = this._sortArtifacts(this._collectMessageArtifacts());
      return;
    }

    this._artifactsSubscription?.unsubscribe();
    this._requestedArtifactsSessionId = sessionId;
    this._artifactsSubscription = this._aiAgentService
      .getSessionArtifacts(sessionId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          if (this._isStaleArtifactsResponse(sessionId)) {
            return;
          }
          this.chatArtifacts = response.succeeded
            ? this._sortArtifacts(this._mergeArtifacts(response.data ?? [], this._collectMessageArtifacts()))
            : this._sortArtifacts(this._collectMessageArtifacts());
          if (response.succeeded) {
            this.artifactsLoaded$.emit(response.data ?? []);
          }
          this._cdr.markForCheck();
        },
        error: () => {
          if (this._isStaleArtifactsResponse(sessionId)) {
            return;
          }
          this.chatArtifacts = this._sortArtifacts(this._collectMessageArtifacts());
          this._cdr.markForCheck();
        }
      });
  }

  /** A response that arrives after the user moved on belongs to another chat and must not touch the counter. */
  private _isStaleArtifactsResponse(sessionId: string): boolean {
    return sessionId !== this._requestedArtifactsSessionId;
  }

  private _collectMessageArtifacts(): DEOAgentArtifact[] {
    return this.messages.flatMap(message => message.artifacts || []);
  }

  private _mergeArtifacts(primaryArtifacts: DEOAgentArtifact[], fallbackArtifacts: DEOAgentArtifact[]): DEOAgentArtifact[] {
    const artifactMap = new Map<string, DEOAgentArtifact>();

    // The same artifact can arrive as a live streamed copy (throwaway id, empty messageId, unnormalized type)
    // and as its persisted backend copy (real id/messageId, normalized type). Those fields differ between the
    // two, so we identify duplicates by their stable content instead: identical HTML body, or failing that
    // title + session. The first copy seen for a given key wins, so the persisted backend copy is kept.
    [...primaryArtifacts, ...fallbackArtifacts].forEach(artifact => {
      const uniqueKey = artifact.htmlContent || [artifact.title, artifact.sessionId].filter(Boolean).join('|');
      if (!artifactMap.has(uniqueKey)) {
        artifactMap.set(uniqueKey, artifact);
      }
    });

    return Array.from(artifactMap.values());
  }

  private _sortArtifacts(artifacts: DEOAgentArtifact[]): DEOAgentArtifact[] {
    return [...artifacts].sort((leftArtifact, rightArtifact) => {
      const leftTime = this._toTimestamp(leftArtifact.createdAt);
      const rightTime = this._toTimestamp(rightArtifact.createdAt);

      if (leftTime !== rightTime) {
        return rightTime - leftTime;
      }

      return (leftArtifact.title || '').localeCompare(rightArtifact.title || '');
    });
  }

  private _toTimestamp(dateValue: Date | string | undefined): number {
    if (!dateValue) {
      return 0;
    }

    const timestamp = new Date(dateValue).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private _scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
        this._shouldAutoScroll = false;
      }
    } catch (err) {
      console.warn(err);
    }
  }

  private _checkIfAtBottom(): void {
    if (this.messagesContainer) {
      const element = this.messagesContainer.nativeElement;
      const threshold = 50;
      this._shouldAutoScroll = element.scrollTop + element.clientHeight >= element.scrollHeight - threshold;
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSendMessage();
    }
  }

  trackByMessageId(index: number, message: ChatMessage): string {
    return message.id || `message-${index}`;
  }

  formatTime(date: Date | undefined): string {
    return formatChatMessageTimestamp(date);
  }

  formatMessageContent(content: string): string {
    return this._messageFormatter.formatContent(content);
  }

  likeMessage(message: ChatMessage): void {
    if (message.role !== 'assistant' || message.isTyping) {
      return;
    }

    this._feedbackBeforeSubmit.set(message, message.isPositiveFeedback);
    message.isPositiveFeedback = true;
  }

  dislikeMessage(message: ChatMessage): void {
    if (message.role !== 'assistant' || message.isTyping) {
      return;
    }

    this._feedbackBeforeSubmit.set(message, message.isPositiveFeedback);
    message.isPositiveFeedback = false;
  }

  isFeedbackBusy(message: ChatMessage): boolean {
    return this._feedbackInFlight.has(message);
  }

  clearFeedback(message: ChatMessage): void {
    if (message.role !== 'assistant' || message.isTyping) {
      return;
    }

    this._feedbackBeforeSubmit.set(message, message.isPositiveFeedback);
    message.isPositiveFeedback = null;

    if (!this.currentSessionId || !message.id) {
      this._feedbackBeforeSubmit.delete(message);
      return;
    }

    this._feedbackInFlight.add(message);
    this._aiAgentService.deleteFeedback(message.id).pipe(takeUntil(this._destroy$)).subscribe(this.buildFeedbackRemovalObserver(message));
  }

  onLikeFeedbackSubmit(message: ChatMessage, details: string): void {
    if (!this.currentSessionId || !message.id) {
      return;
    }

    this._feedbackInFlight.add(message);
    this._aiAgentService
      .sendFeedback({
        message_id: message.id,
        is_positive: true,
        feedback: details
      })
      .subscribe(this.buildFeedbackObserver(message));
  }

  onDislikeFeedbackSubmit(message: ChatMessage, feedback: DislikeFeedback): void {
    if (!this.currentSessionId || !message.id) {
      return;
    }

    this._feedbackInFlight.add(message);
    this._aiAgentService
      .sendFeedback({
        message_id: message.id,
        is_positive: false,
        feedback: feedback.details,
        issue_type_id: feedback.issueTypeId
      })
      .subscribe(this.buildFeedbackObserver(message));
  }

  /**
   * Confirms feedback only once the backend accepted it, so a failed POST never shows a success toast.
   *
   * The thumb is filled optimistically the moment it is clicked, which would otherwise outlive a rejection
   * and disagree with the server until the next reload — the rating the user can see is then one the
   * backend never stored. A rejection therefore restores the previous thumb alongside the error toast.
   * Rejections arrive two ways: as an HTTP error, and as `succeeded: false` on a 200 (an unknown message
   * id, for instance), which carries the reason in the same envelope field.
   */
  private buildFeedbackObserver(message: ChatMessage): { next: (response: IResponse<Feedback>) => void; error: (error: unknown) => void } {
    return {
      next: response => {
        this._feedbackInFlight.delete(message);
        if (response?.succeeded === false) {
          this.rejectFeedback(message, getResponseErrorMessage(response, FEEDBACK_FAILED_MESSAGE));
          return;
        }
        this._feedbackBeforeSubmit.delete(message);
        this._gainsToastService.success('Feedback Sent', 'Thank you for your feedback!');
      },
      error: error => {
        this._feedbackInFlight.delete(message);
        this.rejectFeedback(message, getApiErrorMessage(error, FEEDBACK_FAILED_MESSAGE));
      }
    };
  }

  /** Removing a rating is confirmed by the thumb emptying, so an accepted delete stays silent. */
  private buildFeedbackRemovalObserver(message: ChatMessage): { next: (response: IResponse<boolean>) => void; error: (error: unknown) => void } {
    return {
      next: response => {
        this._feedbackInFlight.delete(message);
        if (response?.succeeded === false) {
          this.rejectFeedback(message, getResponseErrorMessage(response, FEEDBACK_REMOVE_FAILED_MESSAGE));
          return;
        }
        this._feedbackBeforeSubmit.delete(message);
        this._cdr.markForCheck();
      },
      error: error => {
        this._feedbackInFlight.delete(message);
        this.rejectFeedback(message, getApiErrorMessage(error, FEEDBACK_REMOVE_FAILED_MESSAGE));
      }
    };
  }

  private rejectFeedback(message: ChatMessage, reason: string | undefined): void {
    if (this._feedbackBeforeSubmit.has(message)) {
      message.isPositiveFeedback = this._feedbackBeforeSubmit.get(message);
      this._feedbackBeforeSubmit.delete(message);
    }
    this._cdr.markForCheck();
    this._gainsToastService.error('Error', reason?.trim() || FEEDBACK_FAILED_MESSAGE);
  }

  async copyMessage(message: ChatMessage): Promise<void> {
    const messageText = message.content?.trim();
    if (!messageText) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(messageText);
      } else {
        const copied = this._copyTextUsingTextarea(messageText);
        if (!copied) {
          throw new Error('Clipboard API unavailable');
        }
      }

      this._gainsToastService.success('Copied', 'Message copied to clipboard');
    } catch {
      this._gainsToastService.error('Error', 'Unable to copy message');
    }
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    this.selectedFile = this._fileSelectionService.beforeUpload(file, this.MAX_FILE_SIZE);
    this._cdr.markForCheck();
    return false;
  };

  removeFile(): void {
    this.selectedFile = this._fileSelectionService.removeFile();
    this._cdr.markForCheck();
  }

  formatFileSize(bytes: number): string {
    return this._fileSelectionService.formatFileSize(bytes);
  }

  getFileIcon(fileName: string): string {
    return this._fileSelectionService.getFileIcon(fileName);
  }

  getArtifactIcon(type: string): string {
    return getArtifactIcon(type);
  }

  getArtifactTypeColor(type: string): string {
    return getArtifactTypeColor(type);
  }

  onToggleVoiceInput(): void {
    if (this.isLoading || this.isStreaming) {
      return;
    }

    if (this.isListening) {
      this._voiceService.stopListening();
      return;
    }

    this.voiceError = null;
    this._baseText = this.currentMessage;
    this._voiceService.startListeningFromConfiguration();
  }

  onViewArtifact(artifact: DEOAgentArtifact): void {
    this._deoAiAssistantService.openArtifactPreview({
      id: artifact.id,
      title: artifact.title,
      type: artifact.type,
      htmlContent: artifact.htmlContent || '',
      createdAt: artifact.createdAt
    });
  }

  getTotalArtifactCount(): number {
    return this.chatArtifacts.length;
  }

  trackByArtifactId(_: number, artifact: DEOAgentArtifact): string {
    return artifact.id;
  }

  getArtifactTypeLabel(type: string | undefined): string {
    return type?.trim() || 'unknown';
  }

  onArtifactCardClick(artifact: DEOAgentArtifact): void {
    this.isArtifactsPanelOpen = false;
    this.onViewArtifact(artifact);
  }

  onEllipsisClick(event: MouseEvent): void {
    event.stopPropagation();
    this.isEllipsisActive = !this.isEllipsisActive;
  }

  onEllipsisMenuAction(action?: string): void {
    this.isEllipsisActive = false;
    if (action === 'Star' || action === 'Unstar') {
      this.toggleCurrentChatFavorite();
    } else if (action === 'Rename') {
      this.openRenameChatModal();
    } else if (action === 'Move to Project') {
      this.openMoveToProjectModal();
    } else if (action === 'Delete') {
      this.confirmDeleteCurrentChat();
    }
  }

  private confirmDeleteCurrentChat(): void {
    if (!this.currentSessionId) {
      return;
    }

    this._gainsApplyCancelService.delete(
      'Delete Chat',
      `<div><div>Are you sure you want to delete chat "${this.chatTitle}"? This action cannot be undone.</div><div class="padding-top-10">Are you sure you want to proceed?</div></div>`,
      () => this.deleteCurrentChat(),
      () => {
        // Cancel action
      }
    );
  }

  private deleteCurrentChat(): void {
    const sessionId = this.currentSessionId;
    if (!sessionId) {
      return;
    }

    if (isTempSessionId(sessionId)) {
      this.chatDeleted$.emit(sessionId);
      this._gainsToastService.success('Success', 'Chat deleted successfully');
      return;
    }

    this._aiAgentService
      .deleteChatSession(sessionId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          if (!response.succeeded) {
            this._gainsToastService.error('Error', 'Failed to delete chat. Please try again.');
            return;
          }
          this.chatDeleted$.emit(sessionId);
          this._gainsToastService.success('Success', 'Chat deleted successfully');
        },
        error: error => {
          this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to delete chat. Please try again.'));
        }
      });
  }

  private openMoveToProjectModal(): void {
    if (!this.currentSessionId) {
      return;
    }

    const modalRef = this._modalService.create({
      nzContent: DeoAiAssistantMoveToProjectComponent,
      nzFooter: null,
      nzClosable: true,
      nzCentered: true,
      nzWidth: 540,
      nzData: { sessionCount: 1 },
      nzBodyStyle: { padding: '0' }
    });

    modalRef.afterClose.pipe(takeUntil(this._destroy$)).subscribe((result: MoveToProjectModalResult | undefined) => {
      if (!result) {
        return;
      }
      this.moveCurrentChatToProject(result.projectId);
    });
  }

  private moveCurrentChatToProject(projectId: string | null): void {
    if (!this.currentSessionId) {
      return;
    }

    this._aiAgentService
      .setSessionProject(this.currentSessionId, projectId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          if (!response.succeeded) {
            this._gainsToastService.error('Error', 'Failed to move chat. Please try again.');
            return;
          }
          this._gainsToastService.success('Chat Moved', `The chat <strong>"${this.chatTitle}"</strong> has been moved to the selected project.`);
        },
        error: error => {
          this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to move chat. Please try again.'));
        }
      });
  }

  private openRenameChatModal(): void {
    if (!this.currentSessionId) {
      return;
    }

    const modalRef = this._modalService.create({
      nzContent: DeoAiAssistantRenameChatComponent,
      nzFooter: null,
      nzClosable: true,
      nzCentered: true,
      nzWidth: 540,
      nzData: { sessionId: this.currentSessionId, originalChatName: this.chatTitle },
      nzBodyStyle: { padding: '0' }
    });

    modalRef.afterClose.pipe(takeUntil(this._destroy$)).subscribe((result: RenameChatModalResult | undefined) => {
      if (!result?.newChatName) {
        return;
      }
      this.renameCurrentChat(result.sessionId, result.newChatName);
    });
  }

  private renameCurrentChat(sessionId: string, newTitle: string): void {
    const previousTitle = this.chatTitle;

    this._aiAgentService
      .renameSession(sessionId, newTitle)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          if (!response.succeeded) {
            this._gainsToastService.error('Error', 'Failed to rename chat. Please try again.');
            return;
          }
          this.chatTitle = newTitle;
          this.chatRenamed$.emit({ sessionId, newTitle });
          this._gainsToastService.success('Chat Renamed', `The chat <strong>"${previousTitle}"</strong> has been renamed to <strong>"${newTitle}"</strong>.`);
        },
        error: error => {
          this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to rename chat. Please try again.'));
        }
      });
  }

  private toggleCurrentChatFavorite(): void {
    if (!this.currentSessionId) {
      return;
    }
    this.isFavorite = !this.isFavorite;
    this._aiAgentService.updateFavorite(this.currentSessionId, this.isFavorite);
    this.favoriteChanged.emit(this.isFavorite);
    this.updateFavoriteMenuItem();

    const title = this.isFavorite ? 'Chat Starred' : 'Chat Unstarred';
    const message = this.isFavorite ? `The chat <strong>"${this.chatTitle}"</strong> has been starred.` : `The chat <strong>"${this.chatTitle}"</strong> has been unstarred.`;
    this._gainsToastService.success(title, message);
  }

  /** Labels the favorite menu item "Unstar" with a filled icon when the chat is starred, "Star" with an outlined icon otherwise. */
  private updateFavoriteMenuItem(): void {
    this.currentChatMenuItems = this.currentChatMenuItems.map(item =>
      this.isFavoriteMenuItem(item) ? { ...item, title: this.isFavorite ? 'Unstar' : 'Star', icon: this.isFavorite ? 'favorite-filled.svg' : 'favorite.svg' } : item
    );
  }

  private isFavoriteMenuItem(item: EllipsisMenuItem): boolean {
    return item.icon === 'favorite.svg' || item.icon === 'favorite-filled.svg';
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isEllipsisActive = false;
  }

  private _copyTextUsingTextarea(text: string): boolean {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } finally {
      document.body.removeChild(textArea);
    }

    return copied;
  }
}
